"use client";

import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { IconButton, Switch, Tooltip } from "@mui/material";
import React from "react";

import { TableRow } from "@/components/molecules/Table/styled";
import {
  getCustomerTemplateStatusLabel,
  getCustomerTemplateTypeLabel,
  isLegacyCustomerTemplateType,
} from "@/constants/message-template.constants";
import SWR_KEYS from "@/constants/swr-keys";
import { formatDate } from "@/helpers/date-helpers";
import useUpdateMessageTemplate from "@/hooks/requests/message-template/use-update-message-template";
import useMutate from "@/hooks/use-mutate";
import usePopOver from "@/hooks/use-popover";
import { ICustomerTemplate } from "@/interfaces/customer-template.interface";
import { MessageTemplateMessages } from "@/messages/message-template/message-template.messages";

import { MessageTemplatePopover } from "./popover";
import { NoWrapTableCell } from "./styled";

type Props = {
  template: ICustomerTemplate;
  messages: MessageTemplateMessages;
  index: number;
};

const safeDate = (value?: string | null) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return formatDate(parsed);
};

export function MessageTemplateTableRow({ template, index, messages }: Props) {
  const { update, isLoading } = useUpdateMessageTemplate();
  const { mutate } = useMutate();
  const {
    openPopover,
    closePopover,
    anchorEl,
    isOpen,
    selected: selectedTemplate,
  } = usePopOver<ICustomerTemplate>();

  const notificationOn = template.notificationOn !== false;

  const handleToggleNotificationOn = async () => {
    await update({
      _id: template.id,
      formData: {
        text: template.text,
        messageTemplate: template.messageTemplate ?? "",
        notificationOn: !notificationOn,
      },
      successMessage: messages.updateSuccess,
      errorMessage: messages.updateError,
      onSuccess: () => {
        void mutate(SWR_KEYS.getMessageTemplates);
        void mutate(SWR_KEYS.getMessageTemplateById);
      },
    });
  };

  return (
    <TableRow index={index}>
      <NoWrapTableCell>{template.id}</NoWrapTableCell>
      <NoWrapTableCell>{template.platformName || "-"}</NoWrapTableCell>
      <NoWrapTableCell>
        {isLegacyCustomerTemplateType(template.type) ? (
          <Tooltip title={messages.legacyTypeWarning}>
            <span>{`${template.type} (descontinuado)`}</span>
          </Tooltip>
        ) : (
          getCustomerTemplateTypeLabel(template.type)
        )}
      </NoWrapTableCell>
      <NoWrapTableCell>{getCustomerTemplateStatusLabel(template.status)}</NoWrapTableCell>
      <NoWrapTableCell>
        <Switch
          checked={notificationOn}
          disabled={isLoading}
          onClick={(e) => {
            e.stopPropagation();
            void handleToggleNotificationOn();
          }}
        />
      </NoWrapTableCell>
      <NoWrapTableCell>{safeDate(template.rowDate)}</NoWrapTableCell>
      <NoWrapTableCell>
        <IconButton onClick={(e) => openPopover(e, template)}>
          <MoreHorizIcon />
        </IconButton>
        {selectedTemplate && (
          <MessageTemplatePopover
            isOpen={isOpen}
            anchorEl={anchorEl}
            onClose={closePopover}
            messages={messages}
            selectedTemplate={selectedTemplate}
          />
        )}
      </NoWrapTableCell>
    </TableRow>
  );
}
